import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuildConfig, useGuildPatch } from '../../../lib/auditQueries.js';
import { useAddBlocklist, useDeleteBlocklist, useSpamBlocklist } from '../../../lib/spamQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';

export const Route = createFileRoute('/guilds/$guildId/spam')({
  component: SpamSettingsPage,
});

interface FormState {
  spamDetectionEnabled: boolean;
  spamShadowMode: boolean;
  spamAutoDeleteThreshold: number;
  spamUntrustedMaxAccountAgeDays: number;
}

function SpamSettingsPage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const config = useGuildConfig(guildId);
  const patch = useGuildPatch(guildId);

  const [state, setState] = useState<FormState | null>(null);

  // Hydrate local form state once the config lands.
  useEffect(() => {
    if (config.data && !state) {
      setState({
        spamDetectionEnabled: config.data.spamDetectionEnabled,
        spamShadowMode: config.data.spamShadowMode,
        spamAutoDeleteThreshold: config.data.spamAutoDeleteThreshold,
        spamUntrustedMaxAccountAgeDays: config.data.spamUntrustedMaxAccountAgeDays,
      });
    }
  }, [config.data, state]);

  if (!state) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100">
        <Header guildId={guildId} />
        <p className="mx-auto max-w-3xl px-6 py-8 text-sm text-zinc-400">{t('common.loading')}</p>
      </div>
    );
  }

  const thresholdPct = Math.round(state.spamAutoDeleteThreshold * 100);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header guildId={guildId} />
      <main className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-zinc-400">{t('spam.subtitle')}</p>
          <Link
            to="/guilds/$guildId/spam-queue"
            params={{ guildId }}
            className="shrink-0 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
          >
            {t('spam.reviewQueueLink')}
          </Link>
        </div>

        <div className="space-y-5 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <ToggleRow
            label={t('spam.toggle.enable')}
            hint={t('spam.toggle.enableHint')}
            checked={state.spamDetectionEnabled}
            onChange={(v) => {
              setState((s) => (s ? { ...s, spamDetectionEnabled: v } : s));
            }}
          />

          <ToggleRow
            label={t('spam.toggle.shadow')}
            hint={t('spam.toggle.shadowHint')}
            checked={state.spamShadowMode}
            disabled={!state.spamDetectionEnabled}
            onChange={(v) => {
              setState((s) => (s ? { ...s, spamShadowMode: v } : s));
            }}
          />

          <Field label={t('spam.field.threshold')} hint={t('spam.field.thresholdHint')}>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={thresholdPct}
                disabled={!state.spamDetectionEnabled}
                onChange={(e) => {
                  const v = Number(e.target.value) / 100;
                  setState((s) => (s ? { ...s, spamAutoDeleteThreshold: v } : s));
                }}
                className="flex-1 accent-indigo-500 disabled:opacity-50"
              />
              <span className="w-12 text-right text-sm tabular-nums text-zinc-300">
                {thresholdPct}%
              </span>
            </div>
          </Field>

          <Field label={t('spam.field.accountAge')} hint={t('spam.field.accountAgeHint')}>
            <input
              type="number"
              min={0}
              max={365}
              value={state.spamUntrustedMaxAccountAgeDays}
              disabled={!state.spamDetectionEnabled}
              onChange={(e) => {
                const v = Math.max(0, Math.min(365, Math.floor(Number(e.target.value) || 0)));
                setState((s) => (s ? { ...s, spamUntrustedMaxAccountAgeDays: v } : s));
              }}
              className="w-28 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm tabular-nums disabled:opacity-50"
            />
          </Field>

          <p className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3 text-xs text-zinc-500">
            {t('spam.infraNote')}
          </p>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                if (config.data)
                  setState({
                    spamDetectionEnabled: config.data.spamDetectionEnabled,
                    spamShadowMode: config.data.spamShadowMode,
                    spamAutoDeleteThreshold: config.data.spamAutoDeleteThreshold,
                    spamUntrustedMaxAccountAgeDays: config.data.spamUntrustedMaxAccountAgeDays,
                  });
              }}
              className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
            >
              {t('spam.button.reset')}
            </button>
            <button
              type="button"
              disabled={patch.isPending}
              onClick={() => {
                patch.mutate(state);
              }}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {patch.isPending ? t('common.loading') : t('spam.button.save')}
            </button>
          </div>
          {patch.isError ? (
            <p className="text-xs text-rose-400">
              {patch.error instanceof Error ? patch.error.message : t('common.error')}
            </p>
          ) : null}
          {patch.isSuccess ? <p className="text-xs text-emerald-400">{t('spam.saved')}</p> : null}
        </div>

        <BlocklistSection guildId={guildId} />
      </main>
    </div>
  );
}

function BlocklistSection({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const list = useSpamBlocklist(guildId);
  const add = useAddBlocklist(guildId);
  const del = useDeleteBlocklist(guildId);
  const [kind, setKind] = useState<'domain' | 'slug_keyword'>('slug_keyword');
  const [value, setValue] = useState('');

  const rows = list.data ?? [];

  return (
    <div className="mt-6 space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
      <div>
        <h3 className="text-sm font-medium">{t('spam.blocklist.title')}</h3>
        <p className="mt-1 text-xs text-zinc-500">{t('spam.blocklist.hint')}</p>
      </div>

      <div className="flex gap-2">
        <select
          className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as 'domain' | 'slug_keyword');
          }}
        >
          <option value="slug_keyword">{t('spam.blocklist.kind.slug_keyword')}</option>
          <option value="domain">{t('spam.blocklist.kind.domain')}</option>
        </select>
        <input
          type="text"
          value={value}
          placeholder={t('spam.blocklist.valuePlaceholder')}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          className="flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm"
        />
        <button
          type="button"
          disabled={!value.trim() || add.isPending}
          onClick={() => {
            add.mutate(
              { kind, value: value.trim() },
              {
                onSuccess: () => {
                  setValue('');
                },
              },
            );
          }}
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {t('spam.blocklist.add')}
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-zinc-500">{t('spam.blocklist.empty')}</p>
      ) : (
        <ul className="divide-y divide-zinc-800/60">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-2 text-sm">
              <span className="flex items-center gap-2">
                <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs text-zinc-400">
                  {t(`spam.blocklist.kind.${r.kind}`)}
                </span>
                <span className="font-mono">{r.value}</span>
                {r.global ? (
                  <span className="text-xs text-zinc-600">{t('spam.blocklist.global')}</span>
                ) : null}
              </span>
              {r.global ? null : (
                <button
                  type="button"
                  disabled={del.isPending}
                  onClick={() => {
                    del.mutate(r.id);
                  }}
                  className="text-xs text-rose-400 hover:text-rose-300 disabled:opacity-50"
                >
                  {t('common.remove')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Header({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const config = useGuildConfig(guildId);
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs
          items={[
            { label: t('app.name'), to: '/' },
            {
              label: config.data?.name ?? guildId,
              to: '/guilds/$guildId',
              params: { guildId },
            },
            { label: t('spam.title') },
          ]}
        />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
        checked={checked}
        disabled={disabled ?? false}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="mt-1 text-xs text-zinc-500">{hint}</p>
      </div>
    </label>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-zinc-400">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
    </div>
  );
}
